import { dateInZone, localDateTime, validatedTimeZone } from './datetime.ts';
import type { RecurrenceConfig } from './types.ts';

type Calendar = RecurrenceConfig & { anchorIndex?: number };
export interface Occurrence {
  index: number;
  dueDate: string;
  scheduledAt: string | null;
}
const DAY = 86_400_000;

function calendarDate(value: string): Date {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    throw new Error('Choose a real start date in YYYY-MM-DD format.');
  const parsed = new Date(`${value}T00:00:00Z`);
  if (
    !Number.isFinite(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value ||
    parsed.getUTCFullYear() < 2000 ||
    parsed.getUTCFullYear() > 2100
  )
    throw new Error('Choose a real start date between 2000 and 2100.');
  return parsed;
}

// Compatible calendar disambiguation: repeated times use the earlier instant;
// nonexistent times move forward by the clock gap. The stored anchor never moves.
function recurringInstant(day: string, time: string, timeZone: string): string {
  const wall = `${day}T${time}`;
  const nominal = Date.parse(`${wall}:00Z`);
  const offsets = new Set<number>();
  for (const hours of [-48, -24, 0, 24, 48]) {
    const probe = nominal + hours * 3_600_000;
    const local = localDateTime(new Date(probe), timeZone);
    offsets.add(Date.parse(`${local}:00Z`) - probe);
  }
  const candidates = [...offsets].map((offset) => {
    const instant = nominal - offset;
    const local = localDateTime(new Date(instant), timeZone);
    return { instant, local, shift: Date.parse(`${local}:00Z`) - nominal };
  });
  const exact = candidates.filter((candidate) => candidate.local === wall);
  if (exact.length) return new Date(Math.min(...exact.map((value) => value.instant))).toISOString();
  const forward = candidates.filter((candidate) => candidate.shift > 0);
  forward.sort((a, b) => a.shift - b.shift || a.instant - b.instant);
  if (!forward.length) throw new Error('Could not resolve this recurring time in its timezone.');
  return new Date(forward[0].instant).toISOString();
}

export function occurrenceAt(config: Calendar, index: number): Occurrence {
  if (!['daily', 'weekly', 'monthly'].includes(config.frequency))
    throw new Error('Repeat daily, weekly or monthly.');
  const timeZone = validatedTimeZone(config.timeZone);
  if (config.localTime !== null && !/^([01]\d|2[0-3]):[0-5]\d$/.test(config.localTime))
    throw new Error('Choose a recurring time in 24-hour HH:mm format.');
  const anchorIndex = config.anchorIndex ?? 0;
  if (
    !Number.isSafeInteger(index) ||
    !Number.isSafeInteger(anchorIndex) ||
    anchorIndex < 0 ||
    index < anchorIndex
  )
    throw new Error('The recurrence occurrence index is invalid.');
  const date = calendarDate(config.anchorDate);
  const offset = index - anchorIndex;
  if (offset > 37000)
    throw new Error('The next occurrence is beyond the supported calendar range.');
  if (config.frequency === 'monthly') {
    const anchorDay = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + offset);
    const lastDay = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
    ).getUTCDate();
    date.setUTCDate(Math.min(anchorDay, lastDay));
  } else {
    date.setUTCDate(date.getUTCDate() + offset * (config.frequency === 'weekly' ? 7 : 1));
  }
  if (date.getUTCFullYear() > 2100)
    throw new Error('The next occurrence is beyond 2100. Stop repeating to complete this item.');
  const dueDate = date.toISOString().slice(0, 10);
  return {
    index,
    dueDate,
    scheduledAt:
      config.localTime === null ? null : recurringInstant(dueDate, config.localTime, timeZone),
  };
}

export function nextOccurrence(
  config: Calendar,
  currentIndex: number,
  now = new Date(),
): Occurrence {
  if (!Number.isFinite(now.getTime())) throw new Error('The current time is invalid.');
  const current = occurrenceAt(config, currentIndex);
  const today = dateInZone(now, config.timeZone);
  const anchor = calendarDate(config.anchorDate);
  const reference = calendarDate(today);
  const approximateOffset =
    config.frequency === 'monthly'
      ? (reference.getUTCFullYear() - anchor.getUTCFullYear()) * 12 +
        reference.getUTCMonth() -
        anchor.getUTCMonth()
      : Math.floor(
          (reference.getTime() - anchor.getTime()) /
            (DAY * (config.frequency === 'weekly' ? 7 : 1)),
        );
  let index = Math.max(currentIndex + 1, (config.anchorIndex ?? 0) + approximateOffset);
  const threshold = Math.max(
    now.getTime(),
    current.scheduledAt ? Date.parse(current.scheduledAt) : 0,
  );
  // The estimate skips years of missed work directly; only calendar/DST boundary
  // corrections remain. A skipped civil day can map two slots to one instant.
  for (let correction = 0; correction < 8; correction++, index++) {
    const candidate = occurrenceAt(config, index);
    if (
      candidate.scheduledAt
        ? Date.parse(candidate.scheduledAt) > threshold
        : candidate.dueDate > today
    )
      return candidate;
  }
  throw new Error('Could not find the next occurrence. Check the repeating schedule.');
}
